import { memo } from 'react'

import type { Image } from '@tribeplatform/gql-client/global-types'
import {
  ExternalSearchResult,
  Member,
  Post,
  Space,
} from '@tribeplatform/gql-client/types'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { IconSize, SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { EntityImage } from '../../common/components/EntityImage.js'
import { MemberAvatarPlaceholder } from '../../common/components/member/MemberAvatarPlaceholder.js'
import {
  ExtendedSmartSearchEntity,
  ExtendedSmartSearchEntityType,
} from '../index.js'
import { SmartSearchEntityType } from '../SmartSearch.types.js'

type Props = {
  item: ExtendedSmartSearchEntity
  entityType?: ExtendedSmartSearchEntityType
  size?: IconSize
}

export const SearchResultItemPreview = memo(
  ({ item, entityType, size: sizeProp }: Props) => {
    const size: IconSize = sizeProp ?? '1.5x'

    switch (entityType) {
      case SmartSearchEntityType.MEMBERS: {
        const member = item as Member

        return (
          <Avatar
            size={size}
            src={(member?.profilePicture as Image)?.url}
            fallback={MemberAvatarPlaceholder}
            name={member?.name}
          />
        )
      }

      case SmartSearchEntityType.SPACES: {
        const space = item as Space

        return (
          <div className="flex items-center justify-center">
            <EntityImage entity={space} size={size} />
          </div>
        )
      }
      case SmartSearchEntityType.POSTS: {
        const post = item as Post

        return (
          <Avatar
            size={size}
            src={(post.owner?.member?.profilePicture as Image)?.url}
            fallback={MemberAvatarPlaceholder}
            name={post.owner?.member?.name}
          />
        )
      }
      case SmartSearchEntityType.EXTERNALS: {
        const external = item as ExternalSearchResult

        return external.iconUrl ? (
          <div className="h-[1.5rem] aspect-square">
            <img src={external.iconUrl} alt={external.title} />
          </div>
        ) : (
          <SearchResultItemPreview item={item} entityType={undefined} />
        )
      }
      case ExtendedSmartSearchEntityType.HISTORY: {
        return (
          <div className="flex items-center justify-center h-[1.5rem] aspect-square bg-surface-subdued rounded-md border border-border-subdued shadow-sm">
            <SvgIcon className="w-4 h-4 text-content-subdued" name="clock" />
          </div>
        )
      }
      default:
        return <SvgIcon className="w-6 h-6 text-content-subdued" name="post" />
    }
  },
)
